import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { MeasuresForm } from "./MeasuresForm/MeasuresForm";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  createInvestigation,
  getCompensations,
  getMeasures,
  getSanctions,
} from "../../../../../../apis";
import { TakenActions } from "./TakenActions/TakenActions";
import { SanctionCompensationForm } from "./SanctionCompensationsForm/SanctionCompensationForm";
import { Confirmation } from "../../../../../Modal/Confirmation/Confirmation";
import { useModalConfirm } from "../../../../../../hooks/useModalConfirm";
import { useReportId } from "../../../../../../context/report/ReportContext";
import { isMultipleSanctionsEnabled } from "../../../../../../config/features/multipleSanctions";

export const SanctionsAndResolutions = ({
  investigationList,
  reportRender,
  offenders,
  victims,
  setActiveTab,
  setIsLoading,
  isLoading,
}) => {
  // Custom Hooks
  const [t] = useTranslation("report");
  const id = useReportId();
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const investigationNameRef = useRef("");
  const { modalConfirm, openModalConfirm, closeModalConfirm } =
    useModalConfirm();

  // Initial State
  const [activeForm, setActiveForm] = useState(null);
  const [measures, setMeasures] = useState([]);
  const [sanctions, setSanctions] = useState([]);

  const [investigationName, setInvestigationName] = useState("");
  const [loadingInvestigation, setLoadingInvestigation] = useState(false);
  const [compensations, setCompensations] = useState([]);
  const multipleEnabled = isMultipleSanctionsEnabled();

  // Enter investigation name
  const setInvName = (value) => {
    investigationNameRef.current = value;
    setInvestigationName(value);
  };
  // Get info from DB
  useEffect(() => {
    getAllMeasures();
    getAllSanctions();
    getAllCompensations();
  }, []);

  // Registered measures
  const getAllMeasures = async () => {
    try {
      await getMeasures(id, "all").then((resp) => {
        setMeasures(resp);
      });
    } catch (error) {
      console.error(error);
    }
  };
  // Sanctions registered
  const getAllSanctions = async () => {
    try {
      await getSanctions(id, "all").then((resp) => {
        // Sin arreglo se conserva lo que habia: el formulario lo sigue usando.
        if (Array.isArray(resp)) setSanctions(resp);
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getAllCompensations = async () => {
    try {
      await getCompensations(id, "all").then((resp) => {
        if (Array.isArray(resp)) setCompensations(resp);
      });
    } catch (error) {
      console.error(error);
    }
  };
  // The form selected is enabled
  const hdlEnableForm = (formIndex) => {
    // Otro equipo pudo registrar algo desde que se cargo la pantalla.
    if (multipleEnabled) {
      if (formIndex === "sanctions") getAllSanctions();
      if (formIndex === "compensations") getAllCompensations();
    }
    setActiveForm(formIndex);
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };
  // Close form
  const closeForm = () => {
    setActiveForm(null);
  };
  // Create aditional investigation
  const additionalInv = async (type) => {
    const dataToSave = {
      id,
      type,
      title: investigationNameRef.current,
    };
    setLoadingInvestigation(true);

    const sending = enqueueSnackbar(`${t("Care.processing_request")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await createInvestigation(dataToSave);
      reportRender();
      closeModalConfirm();
      setInvestigationName("");
      investigationNameRef.current = "";
      enqueueSnackbar(t("Care.investigation_successfully_created"), {
        variant: "success",
      });
      setActiveTab("Investigación");
    } catch (error) {
      console.error(error);
    } finally {
      closeSnackbar(sending);
      setLoadingInvestigation(false);
    }
  };
  // If no investigation has been created
  const createNewInvestigation = () => {
    openModalConfirm({
      variant: "info",
      ethiquin: "inspector",
      title: t("Care.create_investigation"),
      inputLabel: t("Care.investigation_name"),
      description1: t(
        "Resolution.no_individuals_involved_have_been_identified_in_report"
      ),
      description2: t(
        "Resolution.to_proceed_sanction_you_must_first_conduct_investigation_identify_potential_parties_responsible"
      ),
      question: t("Care.what_kind_investigation_want_create"),
      hasInvDescription: true,
      leftButtonColor: "primary",
      leftButtonText: t("Care.advanced"),
      leftIconName: "search_setting_bold",
      leftIconColor: "white",
      leftIconColorHover: "white",
      onClickLeft: () => additionalInv("advance"),
      rightButtonColor: "success",
      rightButtonText: t("Care.basic"),
      rightButtonLabelColor: "white",
      rightIconName: "outline_search",
      rightIconColor: "white",
      rightIconColorHover: "white",
      onClickRight: () => additionalInv("simple"),
    });
  };
  // If there are no registered parties involved
  const goToInvestigations = () => {
    openModalConfirm({
      variant: "warning",
      title: t("Care.warning"),
      titleColor: theme.palette.warning.main,
      description1: t(
        "Resolution.no_individuals_involved_have_been_identified_in_report"
      ),
      description2: t(
        "Resolution.to_proceed_sanction_responsable_individuals_must_first_be_identified_through_investigation"
      ),
      question: t("Care.do_you_want_continue"),
      leftButtonText: t("Care.cancel"),
      leftButtonColor: "secondary",
      onClickLeft: () => closeModalConfirm(),
      rightButtonText: t("Resolution.go_to_investigation"),
      rightButtonColor: "primary",
      onClickRight: () => {
        setActiveTab("Investigación");
        closeModalConfirm();
      },
      ethiquin: "nodata",
    });
  };

  // Determining the available involved parties.
  // Una misma persona puede venir de varias investigaciones (el endpoint la
  // devuelve una vez por cada una), asi que se colapsa a una sola opcion y se
  // acumulan los alias: son las investigaciones donde se le puede buscar
  // propuestas del investigador.
  const getAvaiableInvolvedParty = (involved = [], resolutions = []) => {
    const countRecords = (name) =>
      (resolutions || []).filter((resolution) => resolution.name === name).length;

    // Sin varios registros por persona, quien ya tiene uno deja de ofrecerse.
    const available = (involved || []).filter(
      (person) => multipleEnabled || countRecords(person.name) === 0
    );

    const byName = new Map();
    available.forEach((person) => {
      const current = byName.get(person.name);
      if (!current) {
        const count = countRecords(person.name);
        byName.set(person.name, {
          label: person.name,
          selectLabel:
            count > 0
              ? t("Resolution.person_with_records", { name: person.name, count })
              : person.name,
          value: person.name,
          data: { ...person },
          aliases: person.alias ? [person.alias] : [],
          key: person.name,
        });
        return;
      }
      if (person.alias && !current.aliases.includes(person.alias)) {
        current.aliases.push(person.alias);
      }
    });

    return Array.from(byName.values());
  };

  const getAvaiableOffenders = getAvaiableInvolvedParty(offenders, sanctions);
  const getAvaiableVictims = getAvaiableInvolvedParty(victims, compensations);

  return (
    <>
      <Confirmation
        show={modalConfirm.show}
        variant={modalConfirm.variant}
        onHide={closeModalConfirm}
        title={modalConfirm.title}
        titleColor={modalConfirm.titleColor}
        description1={modalConfirm.description1}
        description2={modalConfirm.description2}
        question={modalConfirm.question}
        loading={isLoading || loadingInvestigation}
        // Left button action
        leftButtonText={modalConfirm.leftButtonText}
        leftButtonColor={modalConfirm.leftButtonColor}
        leftButtonVariant={modalConfirm.leftButtonVariant}
        leftButtonLabelColor={modalConfirm.leftButtonLabelColor}
        leftIconName={modalConfirm.leftIconName}
        leftIconColor={modalConfirm.leftIconColor}
        leftIconColorHover={modalConfirm.leftIconColorHover}
        onClickLeft={modalConfirm.onClickLeft}
        // Right button action
        rightButtonText={modalConfirm.rightButtonText}
        rightButtonColor={modalConfirm.rightButtonColor}
        rightButtonVariant={modalConfirm.rightButtonVariant}
        rightButtonLabelColor={modalConfirm.rightButtonLabelColor}
        rightIconName={modalConfirm.rightIconName}
        rightIconColor={modalConfirm.rightIconColor}
        rightIconColorHover={modalConfirm.rightIconColorHover}
        onClickRight={modalConfirm.onClickRight}
        ethiquin={modalConfirm.ethiquin}
        inputLabel={modalConfirm.inputLabel}
        inputValue={investigationName}
        setInputValue={(value) => setInvName(value)}
        hasInvDescription={modalConfirm.hasInvDescription}
      />
      <Box>
        <Stack
          display="flex"
          alignItems="stretch"
          justifyContent="space-around"
          flexDirection="row"
          flexWrap={{ xs: "wrap", sm: "nowrap" }}
          spacing={1}
          py={1}
        >
          {/* Open to add measurements */}
          <EGButton
            label={t("Resolution.add_measure")}
            iconName="outline_ruler"
            disabled={activeForm !== null && activeForm !== "measures"}
            onClick={() => hdlEnableForm("measures")}
          />
          {/* Open to add sanctions */}
          <EGButton
            label={t("Resolution.add_sanction")}
            iconName="judge_hammer"
            disabled={activeForm !== null && activeForm !== "sanctions"}
            onClick={() => {
              getAvaiableOffenders.length >= 1
                ? hdlEnableForm("sanctions")
                : investigationList.length >= 1
                ? goToInvestigations()
                : createNewInvestigation();
            }}
          />
          {/* Open to add compensations */}
          <EGButton
            label={t("Resolution.add_compensation")}
            iconName="outline_profile_add"
            disabled={activeForm !== null && activeForm !== "compensations"}
            onClick={() => {
              getAvaiableVictims.length >= 1
                ? hdlEnableForm("compensations")
                : investigationList.length >= 1
                ? goToInvestigations()
                : createNewInvestigation();
            }}
          />
        </Stack>
        {/* Forms */}
        {activeForm === "measures" && (
          <MeasuresForm
            closeForm={closeForm}
            reportRender={reportRender}
            getAllMeasures={getAllMeasures}
            investigationList={investigationList}
          />
        )}
        {activeForm === "sanctions" && (
          <SanctionCompensationForm
            closeForm={closeForm}
            reloadInfo={getAllSanctions}
            type={"offenders"}
            people={getAvaiableOffenders}
            reportRender={reportRender}
            investigationList={investigationList}
            records={sanctions}
          />
        )}
        {activeForm === "compensations" && (
          <SanctionCompensationForm
            closeForm={closeForm}
            reloadInfo={getAllCompensations}
            type={"victims"}
            people={getAvaiableVictims}
            reportRender={reportRender}
            records={compensations}
          />
        )}

        {/* Actions and sanctions taken */}
        {!activeForm && (
          <TakenActions
            measures={measures}
            getAllMeasures={getAllMeasures}
            investigationList={investigationList}
            sanctions={sanctions}
            getAllSanctions={getAllSanctions}
            compensations={compensations}
            getAllCompensations={getAllCompensations}
            reportRender={reportRender}
            openModalConfirm={openModalConfirm}
            closeModalConfirm={closeModalConfirm}
            setIsLoading={setIsLoading}
          />
        )}
      </Box>
    </>
  );
};
